import React, { useMemo } from 'react'
import { Button, Col, Row, Spinner } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalyticsTracked } from './hooks/useAnalyticsTracked'
import { showTrackedLinkCreateModal, hideAnalyticsModal } from '../../../actions'
import styles from './AnalyticsTracked.module.css'
import { PLANS } from '../../../constants/plans'

const AnalyticsTracked = ({ domain }) => {
  const productId = useSelector((state) => state.manage.userProfile.productId) || null
  const hasAccess = useMemo(() => {
    return [PLANS.PRO.id, PLANS.PRO_U.id].includes(productId)
  }, [productId])

  const dispatch = useDispatch()
  const analyticsModalPayload = useSelector((state) => state.modals.analyticsModalPayload)
  const { trackedLinks, isLoadingLinks, hasLinks, highlightedLabel, handleUpgrade } = useAnalyticsTracked({
    domain
  })

  const handleCreateLink = () => {
    dispatch(showTrackedLinkCreateModal({ site: analyticsModalPayload.site }))
    dispatch(hideAnalyticsModal())
  }

  if (isLoadingLinks) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className="text-center my-4 bg-light rounded">
        <img src="/assets/img/gifs/tracked-links.gif" width="100%" alt="Tracked Links Preview" />
        <div className={`${styles.trialCTA} mt-2`}>
          <div className="bold">Create unique tracking links for individuals or campaigns</div>
          <Button className="mt-md-0 mt-2" variant="purple" onClick={handleUpgrade}>
            Upgrade to unlock →
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-3">
      {hasLinks ? (
        <>
          <div className="d-flex justify-content-end mb-4">
            <Button variant="primary" onClick={handleCreateLink}>
              Modify links
            </Button>
          </div>

          <Row className="fw-bold d-none d-md-flex pb-2">
            <Col md={2}>Label</Col>
            <Col md={6}>Link</Col>
            <Col md={2} className="text-end">
              Views
            </Col>
            <Col md={2} className="text-end">
              Unique visitors
            </Col>
          </Row>

          <div className={styles.trackedLinksContainer}>
            <div className={styles.trackedLinksList}>
              {trackedLinks.map((link) => (
                <Row
                  key={link.label}
                  className={`align-items-center border mb-2 mb-md-0 py-2 ${
                    highlightedLabel === link.label ? 'bg-light' : ''
                  }`}
                >
                  <Col xs={12} md={2} className="mb-2 mb-md-0 fw-medium text-truncate">
                    {link.label}
                  </Col>
                  <Col xs={12} md={6} className="mb-2 mb-md-0">
                    {link.url}
                  </Col>
                  <Col xs={6} md={2} className="text-md-end">
                    <span className="d-inline d-md-none text-muted small me-1">Views:</span>
                    {link.pageviews}
                  </Col>
                  <Col xs={6} md={2} className="text-md-end">
                    <span className="d-inline d-md-none text-muted small me-1">Unique:</span>
                    {link.visitors}
                  </Col>
                </Row>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="text-center p-5 my-4 bg-light rounded">
          <h5 className="mb-2 bold">No tracked links</h5>
          <p className="text-muted mb-3 mx-auto" style={{ maxWidth: '400px' }}>
            Create unique links for a specific recipient to track when it’s opened
          </p>
          <Button variant="primary" onClick={handleCreateLink}>
            Create link
          </Button>
        </div>
      )}
    </div>
  )
}

export default AnalyticsTracked
